import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, ViewStyle } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { DesktopSideRailChip } from '@/feature/desktop-nav/components/DesktopSideRailChip';
import { CasinoTabNavigationProp } from '@/navigation/casino/types';
import { DESKTOP_CONTENT_TOP_GAP, SIDE_RAIL_CONTENT_GAP, SIDE_RAIL_WIDTH } from '@/utils/constants-platform-specific';

import { useCasinoSideRailCategories } from '../hooks/use-casino-side-rail-categories';
import { useFreeSpinGames } from '../hooks/use-free-spin-games';
import { FOCUS_RING_GUTTER } from '../utils/constants';
import { FAVORITES_TAB_ID, FREE_SPINS_TAB_ID, LOBBY_TAB_ID } from '../utils/utils';

type CasinoDesktopSideRailProps = {
    category?: string;
    isLobby: boolean;
    setSelectedCategoryId: (id: string | null) => void;
    maxHeight?: number;
};

/**
 * left navigation for desktop web casino lobby: replacing TopBar,
 */
export const CasinoDesktopSideRail = ({
    category,
    isLobby,
    setSelectedCategoryId,
    maxHeight,
}: CasinoDesktopSideRailProps) => {
    const navigation = useNavigation<CasinoTabNavigationProp>();
    const { t } = useTranslation('desktop_nav');
    const { sectionCategories, allGamesCategories } = useCasinoSideRailCategories();

    const { data: freeSpinGamesData } = useFreeSpinGames();
    const hasFreeSpins = (freeSpinGamesData?.items.length ?? 0) >= 1;

    const selectLobby = useCallback(() => {
        navigation.setParams({ category: undefined });
        setSelectedCategoryId(LOBBY_TAB_ID);
    }, [navigation, setSelectedCategoryId]);

    const selectCategory = useCallback(
        (id: string, name: string) => {
            navigation.setParams({ category: name });
            setSelectedCategoryId(id);
        },
        [navigation, setSelectedCategoryId]
    );

    return (
        <Box
            width={SIDE_RAIL_WIDTH + FOCUS_RING_GUTTER}
            pr="s12"
            pb="s12"
            gap="s2"
            maxHeight={maxHeight}
            style={styles.rail}
        >
            <DesktopSideRailChip
                label={t('explore')}
                active={isLobby}
                onPress={selectLobby}
                testID="desktop-side-rail-explore"
            />
            {hasFreeSpins ? (
                <DesktopSideRailChip
                    label={t('free_spins')}
                    active={category === 'Free Spins'}
                    onPress={() => selectCategory(FREE_SPINS_TAB_ID, 'Free Spins')}
                    testID="desktop-side-rail-free-spins"
                />
            ) : null}
            <DesktopSideRailChip
                label={t('favorites')}
                active={category === 'Favorites'}
                onPress={() => selectCategory(FAVORITES_TAB_ID, 'Favorites')}
                testID="desktop-side-rail-favorites"
            />
            {sectionCategories.map(section => (
                <DesktopSideRailChip
                    key={section.id}
                    label={section.name}
                    accent={section.isBetrLiveDealer}
                    active={category === section.name}
                    onPress={() => selectCategory(section.id, section.name)}
                    testID={`desktop-side-rail-${section.id}`}
                />
            ))}
            {allGamesCategories.length > 0 ? (
                <Box style={styles.dividerTitle}>
                    <LineSeparator />
                    <Text style={styles.dividerHeading} color="neutral400">
                        {t('all_games')}
                    </Text>
                </Box>
            ) : null}
            {allGamesCategories.map(item => (
                <DesktopSideRailChip
                    key={item.id}
                    label={item.name}
                    accent={item.isBetrLiveDealer}
                    active={category === item.name}
                    onPress={() => selectCategory(item.id, item.name)}
                    testID={`desktop-side-rail-${item.id}`}
                />
            ))}
        </Box>
    );
};

const styles = StyleSheet.create({
    rail: {
        paddingLeft: FOCUS_RING_GUTTER,
        paddingTop: FOCUS_RING_GUTTER,
        marginLeft: -FOCUS_RING_GUTTER,
        marginRight: SIDE_RAIL_CONTENT_GAP,
        marginTop: -FOCUS_RING_GUTTER,
        alignSelf: 'flex-start',
        position: 'sticky' as ViewStyle['position'],
        top: DESKTOP_CONTENT_TOP_GAP - FOCUS_RING_GUTTER,
        overflowY: 'auto',
    } as ViewStyle,

    dividerTitle: {
        gap: 8,
        paddingLeft: 16,
        paddingBottom: 8,
        paddingTop: 7,
    },
    dividerHeading: {
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 16,
        textTransform: 'uppercase',
    },
});
